import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  PenLine, Send, Loader2, CheckCircle2, XCircle, MinusCircle,
} from 'lucide-react';
import { bulkSendOffersForESign } from '../api';

export default function ZohoSignBulkDialog({ open, onOpenChange, offers, onComplete }) {
  const [ccEmails, setCcEmails] = useState('');
  const [notes, setNotes] = useState('');
  const [expirationDays, setExpirationDays] = useState(14);
  const [reminderPeriod, setReminderPeriod] = useState(5);
  const [skipInProgress, setSkipInProgress] = useState(true);
  const [skipNoEmail, setSkipNoEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const targetCount = offers?.length || 0;

  // Show a preview of which offers will be skipped before sending
  const preview = useMemo(() => {
    if (!offers) return { sendable: 0, alreadyInProgress: 0, noEmail: 0 };
    let inProgress = 0, noEmail = 0, sendable = 0;
    for (const o of offers) {
      const st = o.esign?.status;
      if (st === 'SENT' || st === 'VIEWED') { inProgress++; continue; }
      if (!o.candidate_email) { noEmail++; continue; }
      sendable++;
    }
    return { sendable, alreadyInProgress: inProgress, noEmail };
  }, [offers]);

  const handleSend = async () => {
    if (!offers || offers.length === 0) {
      toast.error('No offers selected');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const ccList = ccEmails
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes('@'));
      const { data } = await bulkSendOffersForESign({
        offer_ids: offers.map((o) => o.id),
        cc_emails: ccList,
        notes: notes || undefined,
        expiration_days: Number(expirationDays) || 14,
        reminder_period: Number(reminderPeriod) || 5,
        skip_in_progress: skipInProgress,
        skip_no_email: skipNoEmail,
      });
      setResult(data);
      if (data.sent > 0) {
        toast.success(`✅ ${data.sent} annexure(s) sent for e-signing`);
      }
      if (data.failed > 0) {
        toast.error(`⚠️ ${data.failed} failed — see results below`, { duration: 6000 });
      }
      if (data.sent === 0 && data.failed === 0 && data.skipped > 0) {
        toast.info(`Nothing to send — ${data.skipped} skipped`);
      }
      if (onComplete) onComplete(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Bulk send failed';
      toast.error(detail, { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    setResult(null);
    setCcEmails('');
    setNotes('');
    setExpirationDays(14);
    setReminderPeriod(5);
    setSkipInProgress(true);
    setSkipNoEmail(true);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !sending && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="zoho-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine size={20} className="text-fuchsia-600" />
            Bulk E-Sign Annexures via Zoho Sign
          </DialogTitle>
          <DialogDescription>
            Send personalised salary annexures to <span className="font-semibold">{targetCount}</span> candidate(s) for electronic signature.
          </DialogDescription>
        </DialogHeader>

        {/* Preview / pre-flight check */}
        {!result && (
          <div className="rounded-lg border bg-slate-50 p-4 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600" data-testid="bulk-preview-sendable">
                {preview.sendable}
              </div>
              <div className="text-xs text-slate-600 mt-1">Will be sent</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-600" data-testid="bulk-preview-inprogress">
                {preview.alreadyInProgress}
              </div>
              <div className="text-xs text-slate-600 mt-1">Already in progress</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-400" data-testid="bulk-preview-no-email">
                {preview.noEmail}
              </div>
              <div className="text-xs text-slate-600 mt-1">No email on file</div>
            </div>
          </div>
        )}

        {/* Form */}
        {!result && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <Label htmlFor="bulk-cc">CC (comma-separated, optional)</Label>
              <Input
                id="bulk-cc"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="hr@tnservices.in, manager@tnservices.in"
                data-testid="bulk-cc-emails"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bulk-expiry">Expiration (days)</Label>
                <Input
                  id="bulk-expiry"
                  type="number"
                  min="1"
                  max="180"
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(e.target.value)}
                  data-testid="bulk-expiration-days"
                />
              </div>
              <div>
                <Label htmlFor="bulk-reminder">Reminder every (days)</Label>
                <Input
                  id="bulk-reminder"
                  type="number"
                  min="1"
                  max="30"
                  value={reminderPeriod}
                  onChange={(e) => setReminderPeriod(e.target.value)}
                  data-testid="bulk-reminder-period"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="bulk-notes">Note to recipients (optional)</Label>
              <Textarea
                id="bulk-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Please review and sign your Salary Annexure."
                rows={2}
                data-testid="bulk-notes"
              />
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox
                  checked={skipInProgress}
                  onCheckedChange={(v) => setSkipInProgress(!!v)}
                  data-testid="bulk-skip-in-progress"
                />
                Skip offers already sent for signing (recommended)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox
                  checked={skipNoEmail}
                  onCheckedChange={(v) => setSkipNoEmail(!!v)}
                  data-testid="bulk-skip-no-email"
                />
                Skip offers without a candidate email
              </label>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4 border-t pt-4">
            <div className="grid grid-cols-4 gap-3">
              <Stat label="Total" value={result.total} color="text-slate-700" />
              <Stat label="Sent" value={result.sent} color="text-emerald-600" testid="bulk-result-sent" />
              <Stat label="Skipped" value={result.skipped} color="text-amber-600" testid="bulk-result-skipped" />
              <Stat label="Failed" value={result.failed} color="text-red-600" testid="bulk-result-failed" />
            </div>
            <div className="max-h-72 overflow-y-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Status</th>
                    <th className="text-left p-2 font-medium">Candidate</th>
                    <th className="text-left p-2 font-medium">Email</th>
                    <th className="text-left p-2 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, idx) => (
                    <tr key={r.offer_id || idx} className="border-t" data-testid={`bulk-row-${idx}`}>
                      <td className="p-2">
                        {r.success ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                            <CheckCircle2 size={11} /> Sent
                          </Badge>
                        ) : r.skipped ? (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1">
                            <MinusCircle size={11} /> Skipped
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
                            <XCircle size={11} /> Failed
                          </Badge>
                        )}
                      </td>
                      <td className="p-2 font-medium">{r.candidate_name || '—'}</td>
                      <td className="p-2">{r.recipient_email || '—'}</td>
                      <td className="p-2 text-slate-600">{r.reason || (r.success && r.status) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={sending} data-testid="bulk-close-btn">
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleSend}
              disabled={sending || targetCount === 0}
              className="bg-fuchsia-600 hover:bg-fuchsia-700"
              data-testid="bulk-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="animate-spin mr-1.5" size={16} />
                  Sending {preview.sendable} annexures...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-1.5" />
                  Send {preview.sendable} for E-Signature
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="rounded-lg border bg-white p-3 text-center">
      <div className={`text-xl font-bold ${color}`} data-testid={testid}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}
